"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"
import { DirectionIcon, Typography } from "@/shared/ui/atoms"
import { CustomButton } from "@/shared/ui/molecules"
import type { PartnerHospitalSchedule } from "@/entities/hospital/api/types"
import { useHospitalSchedule } from "@/widgets/hospital-detail/lib/useHospitalOpenState"

interface HospitalScheduleSummaryProps {
  todaySchedule?: PartnerHospitalSchedule
  weeklySchedules: PartnerHospitalSchedule[]
}

const stripDaySuffix = (day: string) => day.replace("요일", "")

/**
 * 오늘 진료 여부 한 줄 + 펼치면 요일별 표.
 *
 * 진료중/진료종료 배지는 마운트 전에는 그리지 않는다(useHospitalSchedule) —
 * 대신 배지 자리를 고정 높이로 잡아 두어 켜질 때 줄이 밀리지 않게 한다.
 */
const HospitalScheduleSummary = ({
  todaySchedule: suppliedTodaySchedule,
  weeklySchedules,
}: HospitalScheduleSummaryProps) => {
  const t = useTranslations()
  const [isWeeklyOpen, setIsWeeklyOpen] = useState(false)
  const { schedule: todaySchedule, openState } = useHospitalSchedule(weeklySchedules, suppliedTodaySchedule)

  if (!suppliedTodaySchedule && weeklySchedules.length === 0) return null

  const isOpen = openState === "open"

  return (
    <div className="flex flex-col gap-2">
      <div className="flex min-h-[22px] flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className={`inline-flex items-center rounded-md px-1.5 py-0.5 transition-opacity ${
            openState === "unknown"
              ? "opacity-0"
              : isOpen
                ? "bg-primary-100 text-primary-700"
                : "bg-gray-100 text-gray-500"
          }`}
        >
          <Typography as="span" size={13} lineHeight={18} className="font-semibold">
            {isOpen ? t("address.open") : t("address.closed")}
          </Typography>
        </span>
        {todaySchedule ? (
          <CustomButton
            className="flex items-center gap-0.5"
            onClick={() => setIsWeeklyOpen((previous) => !previous)}
            ariaLabel={t("schedule.weekly")}
          >
            <Typography size={14} lineHeight={20} className="text-gray-700">
              {stripDaySuffix(todaySchedule.dayType)} {todaySchedule.startTime} - {todaySchedule.endTime}
            </Typography>
            <DirectionIcon size={16} className="text-gray-400" rotate={isWeeklyOpen ? 180 : 0} />
          </CustomButton>
        ) : (
          <Typography size={14} lineHeight={20} className="text-gray-500">
            {t("schedule.unavailable")}
          </Typography>
        )}
      </div>

      {isWeeklyOpen && weeklySchedules.length > 0 ? (
        <ul className="flex flex-col gap-1 rounded-xl bg-gray-100 px-3 py-2.5">
          {weeklySchedules.map((schedule) => {
            const isToday = schedule.dayType === todaySchedule?.dayType
            return (
              <li key={schedule.dayType} className="flex items-center justify-between gap-4">
                <Typography
                  as="span"
                  size={13}
                  lineHeight={20}
                  className={isToday ? "font-semibold text-gray-900" : "text-gray-600"}
                >
                  {stripDaySuffix(schedule.dayType)}
                </Typography>
                <Typography
                  as="span"
                  size={13}
                  lineHeight={20}
                  className={`tabular-nums ${isToday ? "font-semibold text-gray-900" : "text-gray-600"}`}
                >
                  {schedule.startTime} - {schedule.endTime}
                </Typography>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

export default HospitalScheduleSummary
